import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './FCUseCases.css'



const API = process.env.REACT_APP_API_URL || ""

/* ─── Image fetcher — same pattern as FCCompatibility ───────────────────── */
function useUseCaseImages() {
    const [urls, setUrls] = useState({})
    useEffect(() => {
        fetch(`${API}/api/media/fc-usecases`)
            .then(res => res.ok ? res.json() : {})
            .then(map => setUrls(map))
            .catch(() => { })
    }, [])
    return urls
}

/* ─── SVG Fallbacks ──────────────────────────────────────────────────────── */
function HobbyFallback() {
    return (
        <svg viewBox="0 0 300 220" className="fcu-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="175" width="300" height="45" fill="rgba(93,23,158,0.03)" />
            <line x1="0" y1="175" x2="300" y2="175" stroke="rgba(13,10,24,0.07)" strokeWidth="1" />
            {[0, 60, 120, 180, 240, 300].map((x, i) => (
                <line key={i} x1={x} y1="175" x2="150" y2="220"
                    stroke="rgba(13,10,24,0.04)" strokeWidth="1" />
            ))}
            <ellipse cx="150" cy="180" rx="42" ry="6"
                fill="#5d179e" opacity="0.08" />
            <g>
                {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => (
                    <line key={i} x1="150" y1="95"
                        x2={150 + sx * 48} y2={95 + sy * 32}
                        stroke="#5d179e" strokeWidth="2" opacity="0.7" />
                ))}
                {[[102, 63], [198, 63], [102, 127], [198, 127]].map(([x, y], i) => (
                    <g key={i}>
                        <circle cx={x} cy={y} r="12" fill="#f5f2ee" stroke="#5d179e" strokeWidth="1.2" />
                        <circle cx={x} cy={y} r="4" fill="#5d179e" opacity="0.35" />
                        <ellipse cx={x} cy={y} rx="18" ry="4"
                            fill="none" stroke="#5d179e" strokeWidth="1" opacity="0.15" />
                    </g>
                ))}
                <rect x="138" y="83" width="24" height="24" rx="3"
                    fill="#f0ede8" stroke="#5d179e" strokeWidth="1.1" />
                <circle cx="150" cy="95" r="5" fill="#5d179e" opacity="0.45" />
            </g>
            <line x1="202" y1="63" x2="240" y2="44"
                stroke="#5d179e" strokeWidth="0.8" strokeDasharray="3 3" opacity="0.35" />
            <rect x="240" y="32" width="52" height="18" rx="2"
                fill="white" stroke="rgba(93,23,158,0.25)" strokeWidth="1" />
            <text x="266" y="45" textAnchor="middle"
                fill="#5d179e" fontSize="9" fontFamily="IBM Plex Mono">ALT 12m</text>
            {[['ROLL', 0.62], ['PTCH', 0.44], ['YAW', 0.28]].map(([lbl, pct], i) => (
                <g key={i} transform={`translate(10,${186 + i * 12})`}>
                    <text x="0" y="9" fill="rgba(13,10,24,0.3)" fontSize="7" fontFamily="IBM Plex Mono">{lbl}</text>
                    <rect x="28" y="2" width="70" height="5" rx="1" fill="rgba(93,23,158,0.07)" />
                    <rect x="28" y="2" width={70 * pct} height="5" rx="1" fill="#5d179e" opacity="0.45" />
                </g>
            ))}
        </svg>
    )
}

function StemFallback() {
    return (
        <svg viewBox="0 0 300 220" className="fcu-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <rect x="35" y="20" width="230" height="150" rx="6"
                fill="rgba(93,23,158,0.04)" stroke="#5d179e" strokeWidth="1.2" opacity="0.6" />
            {[[35, 20], [265, 20], [35, 170], [265, 170]].map(([x, y], i) => {
                const hw = i % 2 === 0 ? 1 : -1
                const vw = i < 2 ? 1 : -1
                return (
                    <g key={i}>
                        <line x1={x} y1={y} x2={x + hw * 14} y2={y} stroke="#5d179e" strokeWidth="1.4" opacity="0.4" />
                        <line x1={x} y1={y} x2={x} y2={y + vw * 14} stroke="#5d179e" strokeWidth="1.4" opacity="0.4" />
                    </g>
                )
            })}
            {['M60,65 H120 V95 H175', 'M60,95 H95 V135 H195', 'M175,65 H240 V105',
                'M195,135 H240 V155 H195', 'M95,155 H60 V125'].map((d, i) => (
                    <path key={i} d={d} fill="none" stroke="#5d179e" strokeWidth="0.9"
                        opacity="0.2" strokeDasharray="4 3" />
                ))}
            {[[120, 65], [175, 65], [95, 95], [175, 95], [95, 135], [195, 135], [240, 105], [60, 125], [195, 155]].map(([x, y], i) => (
                <g key={i}>
                    <circle cx={x} cy={y} r="4.5" fill="white" stroke="#5d179e" strokeWidth="0.8" opacity="0.5" />
                    <circle cx={x} cy={y} r="1.8" fill="#5d179e" opacity="0.4" />
                </g>
            ))}
            <rect x="118" y="80" width="44" height="44" rx="3"
                fill="white" stroke="#5d179e" strokeWidth="1.2" />
            <rect x="124" y="86" width="32" height="32" rx="1" fill="rgba(93,23,158,0.07)" />
            <text x="140" y="105" textAnchor="middle" fill="#5d179e" fontSize="9" fontFamily="IBM Plex Mono" opacity="0.8">IMU</text>
            <text x="140" y="117" textAnchor="middle" fill="#5d179e" fontSize="7" fontFamily="IBM Plex Mono" opacity="0.45">3-AXIS</text>
            {['GND', 'VCC', 'SDA', 'SCL'].map((l, i) => (
                <text key={i} x={55 + i * 55} y="195"
                    fill="rgba(13,10,24,0.28)" fontSize="8" fontFamily="IBM Plex Mono" textAnchor="middle">{l}</text>
            ))}
        </svg>
    )
}

function RptoFallback() {
    return (
        <svg viewBox="0 0 300 220" className="fcu-svg-fallback" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="175" width="300" height="45" fill="rgba(93,23,158,0.03)" />
            <line x1="0" y1="175" x2="300" y2="175" stroke="rgba(13,10,24,0.07)" strokeWidth="1" />
            {[20, 60, 100, 140, 180, 220, 260].map((x, i) => (
                <rect key={i} x={x} y="184" width="24" height="5" rx="1" fill="rgba(13,10,24,0.05)" />
            ))}
            <path d="M30 175 Q80 55 150 70 Q220 85 270 175"
                fill="none" stroke="#5d179e" strokeWidth="1.2" strokeDasharray="7 5" opacity="0.28" />
            {[[80, 85], [150, 70], [220, 85]].map(([x, y], i) => (
                <g key={i}>
                    <circle cx={x} cy={y} r="6" fill="white" stroke="#5d179e" strokeWidth="1.1" opacity="0.65" />
                    <circle cx={x} cy={y} r="2.5" fill="#5d179e" opacity="0.55" />
                    <text x={x} y={y - 12} textAnchor="middle"
                        fill="#5d179e" fontSize="8" fontFamily="IBM Plex Mono" opacity="0.55">WP{i + 1}</text>
                </g>
            ))}
            <g transform="translate(80,58)">
                {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => (
                    <line key={i} x1="0" y1="0" x2={sx * 18} y2={sy * 12}
                        stroke="#5d179e" strokeWidth="1.7" opacity="0.8" />
                ))}
                {[[-18, -12], [18, -12], [-18, 12], [18, 12]].map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r="6" fill="white" stroke="#5d179e" strokeWidth="0.9" />
                ))}
                <rect x="-7" y="-7" width="14" height="14" rx="2"
                    fill="#f5f2ee" stroke="#5d179e" strokeWidth="0.8" />
                <circle cx="0" cy="0" r="3" fill="#5d179e" opacity="0.55" />
            </g>
            <rect x="185" y="16" width="100" height="68" rx="3"
                fill="white" stroke="rgba(93,23,158,0.22)" strokeWidth="1" />
            <text x="195" y="30" fill="#5d179e" fontSize="7" fontFamily="IBM Plex Mono" opacity="0.65">FLIGHT HUD</text>
            <line x1="185" y1="35" x2="285" y2="35" stroke="rgba(93,23,158,0.12)" strokeWidth="0.8" />
            {[['SPD', '18 m/s'], ['ALT', '28 m'], ['BAT', '82 %']].map(([l, v], i) => (
                <g key={i} transform={`translate(195,${44 + i * 13})`}>
                    <text x="0" y="0" fill="rgba(13,10,24,0.32)" fontSize="8" fontFamily="IBM Plex Mono">{l}</text>
                    <text x="34" y="0" fill="#5d179e" fontSize="8" fontFamily="IBM Plex Mono" opacity="0.8">{v}</text>
                </g>
            ))}
        </svg>
    )
}

/* ─── Visual Slot — image if available, SVG fallback otherwise ───────────── */
function VisualSlot({ imgUrl, Fallback, alt }) {
    if (imgUrl && imgUrl !== 'error') {
        return (
            <div className="fcu-img-slot">
                <img src={imgUrl} alt={alt} className="fcu-img" />
            </div>
        )
    }
    return (
        <div className="fcu-img-slot fcu-img-slot--fallback">
            <Fallback />
        </div>
    )
}

/* ─── Magazine card ──────────────────────────────────────────────────────── */
function MagCard({ data, index, imgUrl, isFeatured }) {
    const cardRef = useRef(null)
    const readyRef = useRef(false)
    const Visual = data.Visual

    useEffect(() => {
        const el = cardRef.current
        if (!el) return
        gsap.fromTo(el,
            { opacity: 0, y: 44 },
            {
                opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
                delay: index * 0.1,
                onComplete: () => { readyRef.current = true },
                scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
            }
        )
    }, [index])

    const onMouseEnter = () => {
        if (!readyRef.current) return
        gsap.to(cardRef.current, { y: -8, scale: 1.015, duration: 0.4, ease: 'power3.out', overwrite: 'auto' })
    }
    const onMouseLeave = () => {
        if (!readyRef.current) return
        gsap.to(cardRef.current, { y: 0, scale: 1, duration: 0.55, ease: 'expo.out', overwrite: 'auto' })
    }

    return (
        <div
            ref={cardRef}
            className={`fcu-mag-card ${isFeatured ? 'fcu-mag-card--featured' : 'fcu-mag-card--mini'}`}
            style={{ '--a': data.accent, '--rgb': data.rgb, opacity: 0 }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {/* Accent top bar */}
            <div className="fcu-mag-bar" />

            {/* Visual area — image or SVG */}
            <div className="fcu-mag-visual">
                <VisualSlot imgUrl={imgUrl} Fallback={data.Fallback} alt={data.name} />
            </div>

            {/* Text overlay / info */}
            <div className="fcu-mag-info">
                <div className="fcu-mag-top">
                    <span className="fcu-mag-tag"
                        style={{ color: data.accent, borderColor: `rgba(${data.rgb},0.32)` }}>
                        {data.tag}
                    </span>
                    <span className="fcu-mag-num">{data.index}</span>
                </div>
                <h3 className="fcu-mag-name">{data.name}</h3>
                <p className="fcu-mag-tagline" style={{ color: data.accent }}>{data.tagline}</p>
                {isFeatured && <p className="fcu-mag-desc">{data.desc}</p>}
                <div className="fcu-mag-badges">
                    {data.badges.map((b, i) => (
                        <span key={i} className={`fcu-badge fcu-badge--${b.type}`}
                            style={b.type === 'live'
                                ? { borderColor: data.accent, color: data.accent }
                                : {}}>
                            {b.type === 'live' &&
                                <span className="fcu-badge-pulse" style={{ background: data.accent }} />}
                            {b.label}
                        </span>
                    ))}
                </div>
            </div>

            <div className="fcu-mag-glow" />
        </div>
    )
}

/* ─── Data ───────────────────────────────────────────────────────────────── */
const CASES = [
    {
        id: 'hobby', index: '01', tag: 'HOBBY FLYING',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcu-hobby',
        name: 'Build. Fly. Repeat.',
        tagline: 'Your first flight starts here.',
        desc: 'Solder your stack, bind your receiver, plug in a 3S LiPo — and you are airborne. No complex ground station, no firmware flashing. Just flying.',
        badges: [
            { label: 'Standard PWM', type: 'info' },
            { label: 'Any RC TX', type: 'info' },
            { label: 'Beginner Safe', type: 'live' },
        ],
        Fallback: HobbyFallback,
    },
    {
        id: 'stem', index: '02', tag: 'STEM EDUCATION',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcu-stem',
        name: 'Learn by Doing.',
        tagline: 'Hands-on embedded engineering from day one.',
        desc: 'Students assemble the drone, observe real IMU data over serial, and fly. Designed for classrooms, workshops and hackathons — open hardware, no gatekeeping.',
        badges: [
            { label: 'Open Hardware', type: 'info' },
            { label: 'Serial Monitor', type: 'info' },
            { label: 'Classroom Safe', type: 'live' },
        ],
        Fallback: StemFallback,
    },
    {
        id: 'rpto', index: '03', tag: 'RPTO TRAINING',
        accent: '#5d179e', rgb: '93,23,158',
        imgKey: 'fcu-rpto',
        name: 'Train Before You Cert.',
        tagline: 'Real stick-time. Safe airframe. Low cost.',
        desc: 'Remote Pilot Training Organisations use Mock Fly FC to give trainees real flight hours on a low-cost airframe before advancing to certified platforms.',
        badges: [
            { label: 'RTF Config', type: 'info' },
            { label: 'Stable Hover', type: 'info' },
            { label: 'RPTO Ready', type: 'live' },
        ],
        Fallback: RptoFallback,
    },
]

/* ─── Section ────────────────────────────────────────────────────────────── */
export default function FCUseCases() {
    const wrapRef = useRef(null)
    const eyebrowRef = useRef(null)
    const h2Ref = useRef(null)
    const subRef = useRef(null)
    const urls = useUseCaseImages()

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(eyebrowRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
                    scrollTrigger: { trigger: eyebrowRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(h2Ref.current, { opacity: 0, y: 60 },
                {
                    opacity: 1, y: 0, duration: 1.1, ease: 'expo.out',
                    scrollTrigger: { trigger: h2Ref.current, start: 'top 85%', toggleActions: 'play none none none' }
                })
            gsap.fromTo(subRef.current, { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: 'expo.out',
                    scrollTrigger: { trigger: subRef.current, start: 'top 88%', toggleActions: 'play none none none' }
                })
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    const [featured, ...minis] = CASES

    return (
        <section ref={wrapRef} className="fcu-wrap">
            <div className="fcu-bg-grid" />
            <div className="fcu-inner">

                <div className="fcu-header">
                    <div ref={eyebrowRef} className="fcu-eyebrow" style={{ opacity: 0 }}>
                        <span className="fcu-dot" />USE CASES
                    </div>
                    <h2 ref={h2Ref} className="fcu-h2" style={{ opacity: 0 }}>
                        Who Flies<br /><em>Mock Fly.</em>
                    </h2>
                    <p ref={subRef} className="fcu-sub" style={{ opacity: 0 }}>
                        From first-time hobbyists to RPTO instructors — one board,
                        three scenarios, infinite flight hours.
                    </p>
                </div>

                {/* Magazine Split Grid */}
                <div className="fcu-mag-grid">
                    {/* Left — large featured card */}
                    <MagCard
                        data={featured}
                        index={0}
                        imgUrl={urls[featured.imgKey]}
                        isFeatured={true}
                    />

                    {/* Right — stacked mini cards */}
                    <div className="fcu-mag-stack">
                        {minis.map((item, i) => (
                            <MagCard
                                key={item.id}
                                data={item}
                                index={i + 1}
                                imgUrl={urls[item.imgKey]}
                                isFeatured={false}
                            />
                        ))}
                    </div>
                </div>

            </div>
        </section>
    )
}